import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { Card } from '@/hooks/useCards';

export type SuggestedCard = Card & { co_occurrence: number };

/**
 * Suggests cards frequently run alongside the user's current deck cards.
 * Looks at all published decks, finds those sharing ≥2 cards with this deck,
 * then ranks the most common cards in those decks that aren't already here.
 */
export function useDeckSuggestions(deckId: string | null, currentCardIds: string[]) {
  return useQuery({
    queryKey: ['deck-suggestions', deckId, currentCardIds.length, currentCardIds.slice().sort().join(',')],
    queryFn: async (): Promise<SuggestedCard[]> => {
      if (!currentCardIds.length) return [];

      // Find published decks that contain any of the current cards
      const { data: matchRows } = await supabase
        .from('deck_cards')
        .select('deck_id, card_id, decks!inner(published, id)')
        .in('card_id', currentCardIds)
        .eq('decks.published', true);

      if (!matchRows?.length) return [];

      // Count overlap per other deck (exclude self)
      const overlapCount = new Map<string, number>();
      for (const r of matchRows as any[]) {
        if (r.deck_id === deckId) continue;
        overlapCount.set(r.deck_id, (overlapCount.get(r.deck_id) || 0) + 1);
      }

      const similarDecks = [...overlapCount.entries()]
        .filter(([, n]) => n >= 2)
        .map(([id]) => id);

      if (!similarDecks.length) return [];

      // Pull all card_ids from those similar decks
      const { data: similarCards } = await supabase
        .from('deck_cards')
        .select('card_id, quantity')
        .in('deck_id', similarDecks);

      if (!similarCards?.length) return [];

      const currentSet = new Set(currentCardIds);
      const freq = new Map<string, number>();
      for (const row of similarCards) {
        if (currentSet.has(row.card_id)) continue;
        freq.set(row.card_id, (freq.get(row.card_id) || 0) + 1);
      }

      const topIds = [...freq.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([id]) => id);

      if (!topIds.length) return [];

      const { data: cards } = await supabase
        .from('cards')
        .select('*')
        .in('id', topIds);

      return (cards || [])
        .map(c => ({ ...(c as Card), co_occurrence: freq.get(c.id) || 0 }))
        .sort((a, b) => b.co_occurrence - a.co_occurrence);
    },
    enabled: !!deckId && currentCardIds.length > 0,
    staleTime: 60_000,
  });
}
